import * as React from 'react';
import { useState, useCallback } from 'react';
import { css } from '@emotion/core';
import LocalDiv from '../../localization/LocalDiv';
import LocalBtn from '../../localization/LocalBtn';
import { Str } from '../../localization/translationKeys';
import Spacer from '../../layout/Spacer';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import { COLORS } from '../../globalStyle/colors';
import { btnStyle } from '../../forms/styles/button';
import { useDispatch } from 'react-redux';
import { useMsgText } from '../../localization/hooks';
import { useToggle } from '../../core/hooks/useToggle';
import { useLoading } from '../../core/api/hooks';
import { ActionType } from '../../core/redux/actionType';
import Msg from '../../localization/Msg';
import * as R from 'ramda';
import { updateDevPlanItemDescriptionAction } from './api/updateDevPlanItemDescriptionAction';
import { useAppState } from '../../core/redux/hooks';
import { TEXT_AREA } from '../../forms/styles/textarea';


const TEXT_AREA_ID = 'item_desc_text_area';

const styles = {
  root:        css`
    background-color: ${COLORS.LIGHTEST_GREY};
    transition: opacity .5s ease;
    border-radius: 10px;
    padding: 30px;
  `,
  loadingForm: css`
    opacity: .4;
  `,
  clickable:   css`
    cursor: pointer;
  `,
  inputGroup:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
    `,
  ],
  label:       css`
    align-self: flex-start;
    color: ${COLORS.DARK_GREY};
    font-size: 12px;
    line-height: 15px;
    font-weight: bold;
    margin-bottom: 5px;
  `,
  textArea:    [
    TEXT_AREA,
    css`
      width: 100%;
      font-size: 13px;
      background-color: ${COLORS.WHITE};
      ::placeholder {
        font-style: italic;
      }
    `,
  ],
  btnRow:      [
    FLEX_ROW,
    css`
      transition: all .5s ease;
      overflow: hidden;
    `,
  ],
  open:        css`
    padding-top: 10px;
    height: 35px;
    opacity: 1;
  `,
  closed:      css`
    height: 0;
    opacity: 0;
  `,
  saveBtn:     [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 80px;
    `,
  ],
  cancelBtn:   [
    btnStyle.tertiary(COLORS.BLUE),
    css`
      min-width: 80px;
    `,
  ],
};


/**
 * Returns the number of new lines in the given maybe string. If the maybe string isn't a string, then you get back 1.
 */
const calcLines = R.memoizeWith(
  (s: string | undefined): string => `${s}`,
  (s: string | undefined): number => s?.split('\n')?.length ?? 1,
);


type Props = {
  participantId: number;
  projectId: number;
  itemId: number;
  desc: string | undefined;
  className?: string;
};


/**
 * DevPlanSummaryText: a component that renders a development item's summary along with form logic to update it.
 */
const DevPlanDescText: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const placeholder = useMsgText(Str.DescriptionNotAdded);

  const [isEditing, toggleIsEditing] = useToggle(false);
  const [desc, setDesc]              = useState<string>(props.desc || '');

  const loading = useLoading(ActionType.UpdateDevPlanItemDescriptionRequest, props.itemId);

  const setEditing = useCallback(
    () => {
      if (isEditing || loading) {
        return;
      }

      toggleIsEditing();

      // Not all browsers properly focus the text area, and some even override focus for the click event.
      // This line: "After the event is processed, set focus on the clicked text area"
      setTimeout(() => document.getElementById(TEXT_AREA_ID)?.focus(), 0);
    },
    [isEditing, loading, toggleIsEditing],
  );

  const onUpdate = React.useCallback(event => setDesc(event.target.value), [setDesc]);

  const onCancel = useCallback(
    event => {
      event.preventDefault();

      if (isEditing) {
        toggleIsEditing();
      }

      setDesc(props.desc || '');
    },
    [isEditing, setDesc, props, toggleIsEditing],
  );

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || props.projectId);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (loading) {
        return;
      }

      dispatch(updateDevPlanItemDescriptionAction(props.participantId, devPlanProjectId, props.itemId, desc));

      if (isEditing) {
        toggleIsEditing();
      }
    },
    [desc, isEditing, toggleIsEditing, loading, props, devPlanProjectId, dispatch],
  );

  return (
    <form
      onReset={onCancel}
      onSubmit={onSubmit}
      css={[styles.root, loading && styles.loadingForm]}
      className={props.className}
    >
      <LocalDiv>
        <label onClick={setEditing} css={[styles.inputGroup, !isEditing && styles.clickable]}>
          <Msg str={Str.Description} css={styles.label} />
          <textarea
            id={TEXT_AREA_ID}
            placeholder={placeholder}
            rows={calcLines(desc)}
            value={desc}
            onChange={onUpdate}
            disabled={!isEditing}
            css={[styles.textArea, !isEditing && styles.clickable]}
          />
        </label>

        {/* Submit/Cancel */}
        <LocalDiv css={[styles.btnRow, isEditing ? styles.open : styles.closed]}>
          <LocalBtn type='submit' str={Str.Save} css={styles.saveBtn} />
          <Spacer sizePx={5} />
          <LocalBtn type='reset' str={Str.Cancel} css={styles.cancelBtn} />
        </LocalDiv>
      </LocalDiv>
    </form>
  );
};

export default DevPlanDescText;
